import React, { useState, useEffect, useCallback } from "react";
import { ShoppingBag, Mail, RefreshCw, Send } from "lucide-react";
import api from "../../config/api";
import toast from "react-hot-toast";

const DEFAULT_SUBJECT = "You left something behind at Knots & Krafts!";
const HOURS_OPTIONS = [
  { value: 24, label: "24 hours" },
  { value: 48, label: "48 hours" },
  { value: 72, label: "3 days" },
  { value: 168, label: "7 days" },
];

const AbandonedCarts = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [customMessage, setCustomMessage] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [sending, setSending] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/admin/abandoned-carts?hours=${hours}`
      );
      setList(response.data.data?.list ?? []);
      setSelectedIds(new Set());
    } catch (error) {
      console.error("Error fetching abandoned carts:", error);
      toast.error("Failed to load abandoned carts");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const toggleSelect = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === list.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(list.map((r) => r.userId)));
    }
  };

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one customer to email");
      return;
    }
    try {
      setSending(true);
      const response = await api.post("/admin/abandoned-carts/send", {
        recipientIds: Array.from(selectedIds),
        subject: subject.trim() || DEFAULT_SUBJECT,
        customMessage: customMessage.trim() || undefined,
        discountCode: discountCode.trim() || undefined,
      });
      const { sentCount, failedCount, errors } = response.data.data;
      if (failedCount > 0 && errors?.length) {
        toast.error(
          `Sent: ${sentCount}, Failed: ${failedCount}. ${errors[0]?.message || ""}`
        );
      } else {
        toast.success(
          `Recovery email sent to ${sentCount} customer${sentCount !== 1 ? "s" : ""}`
        );
      }
      setSelectedIds(new Set());
      fetchList();
    } catch (error) {
      console.error("Error sending recovery emails:", error);
      toast.error(
        error.response?.data?.message || "Failed to send recovery emails"
      );
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Abandoned Carts
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          View customers who left items in their cart and send recovery emails
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="hours" className="text-sm font-medium text-gray-700">
                  Abandoned after:
                </label>
                <select
                  id="hours"
                  value={hours}
                  onChange={(e) => setHours(Number(e.target.value))}
                  className="mt-1 block rounded-md border border-gray-300 py-1.5 pl-2 pr-8 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  {HOURS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={() => fetchList()}
                disabled={loading}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : list.length === 0 ? (
              <div className="px-4 py-12 text-center text-gray-500">
                <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No abandoned carts found for this period.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={
                            list.length > 0 &&
                            selectedIds.size === list.length
                          }
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last activity
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cart total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {list.map((row) => (
                      <tr
                        key={row.userId}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.userId)}
                            onChange={() => toggleSelect(row.userId)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {[row.firstName, row.lastName]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {row.email}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(row.lastCartActivityAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {row.itemCount}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                          ₹{Number(row.cartTotal).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Send recovery email
              </h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select customers from the list, then customize and send the email.
            </p>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Subject
                </label>
                <input
                  id="subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={DEFAULT_SUBJECT}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label
                  htmlFor="customMessage"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Custom message (optional)
                </label>
                <textarea
                  id="customMessage"
                  rows={3}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="e.g. We've added a special discount for you!"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label
                  htmlFor="discountCode"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Discount code (optional)
                </label>
                <input
                  id="discountCode"
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="e.g. COMEBACK10"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={handleSend}
                disabled={selectedIds.size === 0 || sending}
                className="w-full inline-flex justify-center items-center px-4 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send to selected ({selectedIds.size})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbandonedCarts;
